import { useState, useEffect } from "react";
import { Loader2, Copy, Check, DollarSign, Users, MousePointer, TrendingUp, Send, Twitter, Linkedin, MessageCircle, Mail } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AffiliatePage() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/affiliate/me");
      setMe(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(me.share_url);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't access clipboard"); }
  };

  if (loading) return (
    <DashboardLayout eyebrow="Earn with us" title="Affiliate program" subtitle="">
      <div className="flex items-center gap-2 text-zinc-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
    </DashboardLayout>
  );
  if (!me) return null;

  const share = (platform) => {
    const text = encodeURIComponent("I'm using RankPilot AI to rank on Google with AI on autopilot — try it free:");
    const url = encodeURIComponent(me.share_url);
    const links = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      email: `mailto:?subject=${encodeURIComponent("Try RankPilot AI")}&body=${text}%20${url}`,
    };
    window.open(links[platform], "_blank");
  };

  return (
    <DashboardLayout
      eyebrow="Earn with us"
      title="Affiliate program"
      subtitle={`Refer paying customers and earn ${me.commission_rate_pct}% recurring commission for life.`}
    >
      <Tabs defaultValue="overview">
        <TabsList className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="affiliate-tabs">
          <TabsTrigger value="overview" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="referrals" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-referrals">Referrals</TabsTrigger>
          <TabsTrigger value="payouts" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-payouts">Payouts</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6 space-y-5">
          {/* Stats grid */}
          <div className="grid sm:grid-cols-4 gap-3">
            <Stat label="Pending payout" value={`$${me.pending_payout_usd.toFixed(2)}`} icon={DollarSign} color="#10B981" testid="stat-pending" />
            <Stat label="Lifetime earned" value={`$${me.lifetime_earnings_usd.toFixed(2)}`} icon={TrendingUp} color="#0055FF" testid="stat-lifetime" />
            <Stat label="Paying referrals" value={me.paying_referrals} icon={Users} color="#3377FF" testid="stat-paying" />
            <Stat label="Clicks tracked" value={me.clicks} icon={MousePointer} color="#71717A" testid="stat-clicks" />
          </div>

          {/* Share box */}
          <div className="bg-gradient-to-br from-[#0055FF]/10 to-zinc-950 border border-[#0055FF]/30 rounded-sm p-6" data-testid="share-box">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] tracking-[0.22em] uppercase text-[#3377FF]">Your share link</span>
              <Badge className="bg-zinc-900 border border-zinc-800 rounded-sm font-mono text-xs text-zinc-300">{me.code}</Badge>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-3">
              <Input value={me.share_url} readOnly className="h-11 bg-zinc-950 border-zinc-800 rounded-sm font-mono text-xs" data-testid="share-url" onClick={(e) => e.target.select()} />
              <Button onClick={copyLink} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11" data-testid="copy-link-btn">
                {copied ? <><Check className="w-4 h-4 mr-2" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy link</>}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <Button onClick={() => share("twitter")} variant="outline" size="sm" className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="share-twitter">
                <Twitter className="w-3.5 h-3.5 mr-1.5" /> X / Twitter
              </Button>
              <Button onClick={() => share("linkedin")} variant="outline" size="sm" className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="share-linkedin">
                <Linkedin className="w-3.5 h-3.5 mr-1.5" /> LinkedIn
              </Button>
              <Button onClick={() => share("whatsapp")} variant="outline" size="sm" className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="share-whatsapp">
                <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> WhatsApp
              </Button>
              <Button onClick={() => share("email")} variant="outline" size="sm" className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="share-email">
                <Mail className="w-3.5 h-3.5 mr-1.5" /> Email
              </Button>
            </div>
          </div>

          {/* How it works */}
          <div className="grid md:grid-cols-3 gap-3">
            <Howstep n="1" title="Share your link" desc={`Send your unique ref link to anyone — agencies, founders, SEO consultants.`} />
            <Howstep n="2" title="They subscribe" desc={`When they pay for any tier (Starter, Pro, Agency), you earn ${me.commission_rate_pct}% of their monthly bill.`} />
            <Howstep n="3" title="You get paid" desc={`Once your pending payout passes $${me.min_payout_usd}, request a payout via PayPal, Wise, or bank transfer.`} />
          </div>

          <PayoutMethodCard me={me} onChange={load} />
        </TabsContent>

        <TabsContent value="referrals" className="mt-6">
          <ReferralsList />
        </TabsContent>

        <TabsContent value="payouts" className="mt-6">
          <PayoutsList minPayout={me.min_payout_usd} />
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function Stat({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500">
        <Icon className="w-3.5 h-3.5" style={{ color }} /> {label}
      </div>
      <div className="font-display text-2xl font-semibold mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

function Howstep({ n, title, desc }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5">
      <div className="w-7 h-7 rounded-full bg-[#0055FF]/15 text-[#3377FF] grid place-items-center font-mono text-sm font-bold mb-3">{n}</div>
      <div className="font-display text-sm font-semibold mb-1">{title}</div>
      <p className="text-xs text-zinc-500 leading-relaxed">{desc}</p>
    </div>
  );
}

function PayoutMethodCard({ me, onChange }) {
  const existing = me.payout_method || {};
  const [method, setMethod] = useState(existing.method || "paypal");
  const [email, setEmail] = useState(existing.email || me.email);
  const [notes, setNotes] = useState(existing.notes || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/affiliate/payout-method", { method, email, notes });
      toast.success("Payout method saved");
      await onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid="payout-method-card">
      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Payout method</div>
      <div className="grid sm:grid-cols-3 gap-3 items-end">
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Method</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="payout-method-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0B0B0E] border-zinc-800">
              <SelectItem value="paypal">PayPal</SelectItem>
              <SelectItem value="wise">Wise</SelectItem>
              <SelectItem value="bank">Bank transfer</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">{method === "bank" ? "Contact email" : "Account email"}</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="payout-email" />
        </div>
        <div>
          <Button onClick={save} disabled={busy} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11 w-full" data-testid="payout-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </div>
      {method === "bank" && (
        <div className="mt-3">
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Bank details (we'll email you to collect)</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Country, currency, preferred contact"
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="payout-notes" />
        </div>
      )}
    </div>
  );
}

function ReferralsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/affiliate/referrals").then(({ data }) => setItems(data.items || [])).finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />;
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="referrals-list">
      <div className="px-5 py-3 border-b border-zinc-800 text-xs uppercase tracking-widest text-zinc-500 flex justify-between">
        <span>Your referrals</span>
        <span className="text-zinc-600">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="p-8 text-center text-sm text-zinc-500">No referrals yet. Share your link to start earning.</div>
      ) : (
        <div className="divide-y divide-zinc-800">
          {items.map((r, i) => (
            <div key={i} className="px-5 py-3 flex items-center gap-3 text-sm" data-testid={`referral-${i}`}>
              <div className="flex-1 min-w-0 truncate">
                <span className="text-zinc-200 font-mono">{r.referred_email_masked || "—"}</span>
              </div>
              {r.status === "paying" ? (
                <Badge className="bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30 rounded-sm">Paying</Badge>
              ) : (
                <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-500">Signed up</Badge>
              )}
              <span className="text-xs text-zinc-600">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PayoutsList({ minPayout }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/affiliate/payouts").then(({ data }) => setItems(data.items || [])).finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />;
  const pending = items.filter(i => i.status === "pending").reduce((s, i) => s + (i.commission_usd || 0), 0);
  return (
    <div className="space-y-4">
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5 flex items-center gap-4" data-testid="payouts-summary">
        <DollarSign className="w-8 h-8 text-[#10B981]" />
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-zinc-500">Pending payout</div>
          <div className="font-display text-2xl font-semibold text-[#10B981]">${pending.toFixed(2)}</div>
        </div>
        <Button disabled={pending < minPayout} className="bg-[#10B981] hover:bg-[#0d8a64] rounded-sm h-10 text-white" data-testid="request-payout-btn">
          <Send className="w-3.5 h-3.5 mr-1.5" /> {pending < minPayout ? `$${(minPayout - pending).toFixed(2)} to go` : "Request payout"}
        </Button>
      </div>
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="commissions-list">
        <div className="px-5 py-3 border-b border-zinc-800 text-xs uppercase tracking-widest text-zinc-500">Commission history</div>
        {items.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">No commissions yet.</div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {items.map((c, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3 text-sm" data-testid={`commission-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-200 font-mono text-xs">{c.referred_email_masked || "—"}</div>
                  <div className="text-zinc-600 text-xs">Customer paid ${c.amount_paid_usd?.toFixed(2)}</div>
                </div>
                <span className="font-mono text-[#10B981]">+${c.commission_usd?.toFixed(2)}</span>
                <Badge className={c.status === "paid" ? "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30 rounded-sm" : "bg-amber-500/10 text-amber-400 border-amber-700/30 rounded-sm"}>
                  {c.status}
                </Badge>
                <span className="text-xs text-zinc-600">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
